import React, { useState, useEffect } from 'react';
import { Modal, Form, Button, Alert, OverlayTrigger, Tooltip } from 'react-bootstrap';
import { useWriteContract } from 'wagmi';
import membersContractABI from '../../contracts/tgetherMembers.json';

const membersContractAddress = process.env.REACT_APP_MEMBERS_CONTRACT_ADDRESS as `0x${string}`;

interface InviteModalProps {
  show: boolean;
  setShow: (show: boolean) => void;
  communityName: string;
}

const InviteModal = ({ show, setShow, communityName }: InviteModalProps) => {
  const [inviteeAddress, setInviteeAddress] = useState('');
  const [isInvite, setIsInvite] = useState(true); // Toggle between invite/uninvite
  const [showSuccessAlert, setShowSuccessAlert] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');

  // Handle input change for the invitee's address
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setInviteeAddress(e.target.value);
  };

  // Reset form fields and state when the modal is shown or hidden
  useEffect(() => {
    if (show) {
      setInviteeAddress('');
      setIsInvite(true); // Default to invite mode
      setErrorMessage('');
      setShowSuccessAlert(false);
    }
  }, [show]);

  // Close the modal and reset states
  const handleClose = () => {
    setShow(false);
    setInviteeAddress('');
    setErrorMessage('');
  };

  // Contract write function for inviting and uninviting members
  const { writeContract: inviteMemberWrite } = useWriteContract();
  const { writeContract: uninviteMemberWrite } = useWriteContract();

  const handleInviteOrUninvite = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!inviteeAddress) {
      setErrorMessage('Invitee address is required.');
      return;
    }

    try {
      if (isInvite) {
        // Invite a member
        await inviteMemberWrite({
          abi: membersContractABI.abi,
          address: membersContractAddress,
          functionName: 'inviteMember',
          args: [communityName, inviteeAddress],
        });
      } else {
        // Uninvite a member
        await uninviteMemberWrite({
          abi: membersContractABI.abi,
          address: membersContractAddress,
          functionName: 'uninviteMember',
          args: [communityName, inviteeAddress],
        });
      }

      setShow(false);
      setShowSuccessAlert(true); // Show success message on successful invite/uninvite
    } catch (error) {
      console.error('Error inviting/uninviting member:', error);
      setErrorMessage('Failed to perform the action. Please try again.');
    }
  };

  const renderTooltip = (text: string) => (
    <Tooltip id="button-tooltip">{text}</Tooltip>
  );

  return (
    <>
      {showSuccessAlert && (
        <Alert variant="success" onClose={() => setShowSuccessAlert(false)} dismissible>
          {isInvite ? 'Member invited successfully!' : 'Member uninvited successfully!'}
        </Alert>
      )}
      <Modal show={show} onHide={handleClose}>
        <Modal.Header closeButton>
          <Modal.Title>{isInvite ? 'Invite Member' : 'Uninvite Member'} to {communityName}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form onSubmit={handleInviteOrUninvite}>
            <Form.Group>
              <Form.Label>
                Member Address{' '}
                <OverlayTrigger
                  placement="right"
                  overlay={renderTooltip("Enter the Ethereum address of the member.")}
                >
                  <Button variant="secondary" className="ml-1 p-0" style={{ border: "none", background: "none" }}>
                    <i className="bi bi-patch-question-fill"></i>
                  </Button>
                </OverlayTrigger>
              </Form.Label>
              <Form.Control
                type="text"
                placeholder="Enter member address"
                value={inviteeAddress}
                onChange={handleInputChange}
                isInvalid={!!errorMessage} // Highlight the input if there's an error
              />
              {errorMessage && <Form.Control.Feedback type="invalid">{errorMessage}</Form.Control.Feedback>}
            </Form.Group>

            <Form.Group controlId="inviteOrUninvite" className="mt-4">
              <Form.Label>Toggle Action</Form.Label>
              <div className="custom-switch-container d-flex align-items-center">
                <span className="label">Uninvite</span>
                <label className="custom-switch mx-2">
                  <input 
                    type="checkbox" 
                    checked={isInvite} 
                    onChange={() => setIsInvite(!isInvite)} 
                  />
                  <span className="slider"></span>
                </label>
                <span className="label">Invite</span>
              </div>
            </Form.Group>

            <div className="d-flex justify-content-between">
              <Button variant="secondary" onClick={handleClose}>
                Cancel
              </Button>
              <Button variant="primary" type="submit">
                {isInvite ? 'Invite' : 'Uninvite'}
              </Button>
            </div>
          </Form>
        </Modal.Body>
      </Modal>
    </>
  );
};

export default InviteModal;
